import React, { Suspense, useState, useEffect } from "react";
import { Route, Switch, Redirect } from "react-router-dom";
import Signup from "./Signup";
import LoginTestUser from "./LoginTestUser/LoginTestUser";
import WaitList from "./WaitList";
import AppContainer from "./AppContainer/AppContainer";
import ServerDown from "./ServerDown";
import StagingRestricted from "./StagingRestricted";
import AddTokens from "./AddTokens";
import TermsAndCondition from "./TermsAndCondition";
import PrivacyPolicy from "./PrivacyPolicy";
import DMCAPolicy from "./DMCAPolicy";
import HowToUse from "./HowToUse";
import HowToEarn from "./HowToEarn";
// Rarely visited or heavy pages are code-split so listeners never download
// the streaming SDKs, OBS tooling, or admin bundles. The Suspense boundary
// below shows LoadingScreen while a chunk loads.
const AdminPortal = React.lazy(() => import("./Admin/AdminPortal"));
const VideoPlayer = React.lazy(() => import("./VideoPlayer/VideoPlayer"));
import DebugRoute from "./DebugRoute";

const DEV_ONLY_ROUTES =
  process.env.NODE_ENV !== "production"
    ? [
        <Route key="viaduct" path="/viaduct/:userNum" component={LoginTestUser} />,
        <Route key="debug-route" path="/debug-route" component={DebugRoute} />,
      ]
    : null;
import MobileLanding from "./MobileLanding";
import DesktopLanding from "./DesktopLanding";
import PowerPlaysInfo from "./PowerPlaysInfo";
import PowerPlayShare from "./PowerPlayShare";
import SubscribePage from "./Subscribe";
import { shouldShowMobileLanding } from "../utils/mobileDetection";
import LoadingScreen from "../components/LoadingScreen";

import {
  getUserDetail,
  isLoggedInUser,
} from "../constants/function";
import { checkAndRefreshIfNeeded } from "../utils/refreshUserData";
import cookieStorage from "../services/cookie-storage";
const LiveVideoStreaming = React.lazy(() => import("./Streaming/Streaming"));
const AddUsersToStream = React.lazy(() => import("./AddUsersToStream/AddUsersToStream"));

// OBS Browser Source pages (public, API key auth only)
const OBSPlayer = React.lazy(() => import("./OBS/Player"));
const OBSControls = React.lazy(() => import("./OBS/Controls"));
const OBSChat = React.lazy(() => import("./OBS/Chat"));

// Public profit breakdown viewer
const ProfitBreakdownView = React.lazy(() => import("./ProfitBreakdownView/ProfitBreakdownView"));

// Public investor page
const InvestorPage = React.lazy(() => import("./Investor"));
// Public capabilities page for labels, distributors, publishers and sponsors
const CapabilitiesPage = React.lazy(() => import("./Capabilities"));
// Public artist capabilities deck, shown full screen in an iframe
const ArtistsDeckPage = React.lazy(() => import("./ArtistsDeck"));
// Public share page for one jukebox day, where fans request songs for it
const JukeboxDayPage = React.lazy(() => import("./JukeboxDay"));

// Public investor data room (password-protected per investor)
const DataRoomPage = React.lazy(() => import("./DataRoom"));

function isAdminUser() {
  const user = getUserDetail();
  return user && user.roles && user.roles.includes('admin');
}

function isAdminOrModerator() {
  const user = getUserDetail();
  return user && user.roles && (user.roles.includes('admin') || user.roles.includes('moderator'));
}

function PrivateRoute({ component: Component, path, appProps, userData: parentUserData, ...rest }) {
  const unauthorisedUrl = ["/", "Signup"];
  const userData = parentUserData || getUserDetail();
  const [isLoadingUserData, setIsLoadingUserData] = useState(false);
  
  
  
  // If user is logged in but no userData, fetch it from API
  useEffect(() => {
    
    if (isLoggedInUser() && !userData && !isLoadingUserData) {
      setIsLoadingUserData(true);
      
      // Import ApiService dynamically to avoid circular imports
      import('../services/api-service').then(({ default: ApiService }) => {
        // Add timeout for user profile fetch (10 seconds)
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Profile fetch timeout')), 10000)
        );
        
        const fetchProfile = ApiService.getToken().then(() => {
          return ApiService.USER_PROFILE();
        });
        
        Promise.race([fetchProfile, timeoutPromise])
          .then(response => {
            if (response && response.result) {
              localStorage.setItem('userData', JSON.stringify({ userData: response.result }));
              window.location.reload(); // Reload to use the new data
            } else {
              throw new Error('Invalid profile response');
            }
          })
          .catch(error => {
            // If API call fails or times out, clear everything and redirect to login
            cookieStorage.deleteCookie('Authorization');
            localStorage.clear();
            window.location.href = '/';
          })
          .finally(() => {
            setIsLoadingUserData(false);
          });
      }).catch(error => {
        console.error('Failed to import ApiService:', error);
        // If we can't even load the API service, clear and redirect
        cookieStorage.deleteCookie('Authorization');
        localStorage.clear();
        window.location.href = '/';
        setIsLoadingUserData(false);
      });
    }
  }, [userData, isLoadingUserData]);
  
  const routeDebugInfo = {
    path: rest.path,
    isLoggedIn: isLoggedInUser(),
    // isWhiteListed: removed - no longer needed,
    userDataExists: !!userData,
    hasUserId: !!userData?._id,
    hasBirthYear: !!userData?.birthYear,
    birthYear: userData?.birthYear,
    authCookie: !!cookieStorage.getCookie("Authorization"),
    localStorageExists: !!localStorage.getItem("userData")
  };
  
  return (
    <Route
      {...rest}
      render={(props) => {
        if (
          isLoggedInUser() &&
          unauthorisedUrl.includes(props.location.pathname)
        ) {
          return (
            <Redirect
              to={{
                pathname: `/app/home`,
              }}
            />
          );
        } else if (
          isLoggedInUser() &&
          userData &&
          userData._id && // Make sure we have valid user data
          !userData["birthYear"]
        ) {
          // GUARANTEED: Force redirect to signup
          window.location.href = '/signup';
          return null;
        } else if (isLoggedInUser()) {
          // Show loading while fetching user data
          if (isLoadingUserData) {
            return (
              <LoadingScreen 
                message="Loading user data..." 
                submessage="If this takes too long, you'll be redirected to login"
              />
            );
          }
          return <Component {...props} {...appProps} />;
        } else if (isLoggedInUser() && !userData) {
          // User is authenticated but missing localStorage data - redirect to login to refresh
          cookieStorage.deleteCookie('Authorization');
          localStorage.clear();
          return (
            <Redirect
              to={{
                pathname: "/",
              }}
            />
          );
        // Whitelist check removed - no longer needed
        } else {
          return (
            <Redirect
              to={{
                pathname: "/",
              }}
            />
          );
        }
      }}
    />
  );
}

function RouterOutlet(props) {
  const [isReady, setIsReady] = useState(false);
  const [userData, setUserData] = useState(null);
  
  useEffect(() => {
    const initializeApp = async () => {
      if (isLoggedInUser()) {
        const data = getUserDetail();
        setUserData(data);
      }
      setIsReady(true);
    };
    
    initializeApp();
  }, []);
  
  if (!isReady) {
    return <LoadingScreen message="Loading..." />;
  }
  
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Switch>
        <Route
          path="/admin"
          render={(props) => {
            console.log('[Admin Route] Admin route accessed:', {
              pathname: props.location.pathname,
              isLoggedIn: isLoggedInUser(),
              isAdmin: isAdminUser()
            });

            if (isLoggedInUser() && isAdminOrModerator()) {
              return <AdminPortal {...props} />;
            }

            return <Redirect to="/" />;
          }}
        />
      <Route exact path="/" render={(props) => {
        const isLoggedIn = isLoggedInUser();
        // Whitelist no longer needed
        
        // GUARANTEED: If logged in, handle immediately
        if (isLoggedIn) {
          // First try to get userData from localStorage if not in state yet
          const localUserData = userData || getUserDetail();
          
          if (localUserData && localUserData.birthYear) {
            window.location.href = '/app/home'; // Force redirect
            return null;
          } else if (localUserData && !localUserData.birthYear) {
            window.location.href = '/signup'; // Force redirect
            return null;
          } else {
            // No user data yet but logged in - redirect to home and let PrivateRoute handle it
            window.location.href = '/app/home';
            return null;
          }
        }
        
        // Not logged in - show appropriate landing page
        if (shouldShowMobileLanding()) {
          return <MobileLanding {...props} />;
        }
        
        return <DesktopLanding {...props} />;
      }} />
      <Route exact path="/mobile" component={MobileLanding} />
      <Route exact path="/desktop" component={DesktopLanding} />
      <Route exact path={["/subscribe", "/subscribe.html"]} render={(props) => {
        const { isMobile, isSmallScreen } = require('../utils/mobileDetection').detectMobileDevice();
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        if (isMobile && isSmallScreen && !isLocalhost) {
          return <MobileLanding {...props} initialSection="subscribe" />;
        }
        return <SubscribePage {...props} />;
      }} />
      <Route exact path="/WaitList"
        render={(props) => <WaitList {...props} />}
      />
      <Route exact path="/TermsAndCondition"
        render={(props) => <TermsAndCondition {...props} />}
      />
      <Route exact path="/TermsCondition"
        render={(props) => <TermsAndCondition {...props} />}
      />
      <Route exact path="/PrivacyPolicy"
        render={(props) => <PrivacyPolicy {...props} />}
      />
      <Route exact path="/DMCAPolicy"
        render={(props) => <DMCAPolicy {...props} />}
      />
      <Route exact path="/HowToUse"
        render={(props) => <HowToUse {...props} />}
      />
      <Route exact path="/HowToEarn"
        render={(props) => <HowToEarn {...props} />}
      />
      <Route exact path="/powerplays"
        render={(props) => <PowerPlaysInfo {...props} />}
      />
      <Route exact path="/powerplay/:id" component={PowerPlayShare} />
      <Route exact path="/investor"
        render={(props) => <InvestorPage {...props} />}
      />
      <Route exact path="/capabilities"
        render={(props) => <CapabilitiesPage {...props} />}
      />
      <Route exact path="/artists-deck" component={ArtistsDeckPage} />
      <Route exact path="/jukebox/:hostId/:slotId" component={JukeboxDayPage} />
      <Route exact path="/data-room"
        render={(props) => <DataRoomPage {...props} />}
      />
      <Route path="/maintenance" component={ServerDown} />
      <Route path="/staging-restricted" component={StagingRestricted} />
      <Route path="/signup" render={(props) => {
        // GUARANTEED: Check userData from localStorage if state not ready
        if (isLoggedInUser()) {
          const localUserData = userData || getUserDetail();
          
          // If we have complete profile, redirect immediately
          if (localUserData && localUserData.birthYear) {
            window.location.href = '/app/home'; // Force redirect
            return null;
          }
          // User needs to complete profile - show signup page
        }
        
        return <Signup {...props} />;
      }} />
      <Route path="/addUsers/:userCount/toStream/:streamId" component={AddUsersToStream} />
      <Route path="/LiveVideoStreaming" component={LiveVideoStreaming} />
      <Route path="/video/:videoId" component={VideoPlayer} />
      {DEV_ONLY_ROUTES}

      {/* OBS Browser Source Routes - Public, API key auth only */}
      <Route exact path="/obs/player" component={OBSPlayer} />
      <Route exact path="/obs/controls" component={OBSControls} />
      <Route exact path="/obs/chat" component={OBSChat} />

      {/* Public Profit Breakdown Viewer - No auth required */}
      <Route exact path="/profit-breakdown/:token" component={ProfitBreakdownView} />

<Route exact path="/home" render={(props) => {
        // console.log('/home route hit - redirecting to /app/home');
        return <Redirect to="/app/home" />;
      }} />
      
      <PrivateRoute path="/app" component={AppContainer} userData={userData} />
      <PrivateRoute exact path="/add-siki-token" component={AddTokens} userData={userData} />
      
      {/* Fallback route - redirect to signup if no other route matches */}
      <Route render={(props) => {
        // console.log('Fallback route hit for path:', props.location.pathname);
        // Prevent infinite redirects by checking if we're already at root
        if (props.location.pathname === '/') {
          return <Signup {...props} />;
        }
        return <Redirect to="/" />;
      }} />
      </Switch>
    </Suspense>
  );
}

export default RouterOutlet;
